import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X, Download, CheckCircle2, FileText, AlertCircle, ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { nactiPdfjs } from '../lib/pdfjs';
import ZoomPanViewer from './ui/ZoomPanViewer';

const SECTION_COLOR = '#EF4444';

const formatDate = (d) => {
    if (!d) return '';
    return new Date(d).toLocaleDateString('cs-CZ', { day: 'numeric', month: 'short', year: 'numeric' });
};

const formatFileSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1048576) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${(bytes / 1048576).toFixed(1)} MB`;
};


// ── Mobile PDF renderer using pdfjs-dist (canvas) ──
// `strana` + `onStrana` + `onPocet` = stránkování ovládá rodič (plovoucí ovládání
// celoobrazovkového náhledu); bez nich si viewer kreslí vlastní lištu dole.
// `odsazeni` = místo nahoře/dole pro plovoucí tlačítka, ať nezakrývají text.
function MobilePdfViewer({ url, strana, onStrana, onPocet, odsazeni }) {
    const canvasRef = useRef(null);
    const containerRef = useRef(null);
    const [pdfDoc, setPdfDoc] = useState(null);
    const [vlastniStrana, setVlastniStrana] = useState(1);
    const rizeno = strana != null && !!onStrana;
    const currentPage = rizeno ? strana : vlastniStrana;
    const setCurrentPage = rizeno ? onStrana : setVlastniStrana;
    const [totalPages, setTotalPages] = useState(0);
    const [rendering, setRendering] = useState(false);
    const [chyba, setChyba] = useState(false);
    const renderTaskRef = useRef(null);

    useEffect(() => {
        let cancelled = false;
        const loadPdf = async () => {
            const pdfjsLib = await nactiPdfjs();
            try {
                const doc = await pdfjsLib.getDocument(url).promise;
                if (!cancelled) {
                    setPdfDoc(doc);
                    setTotalPages(doc.numPages);
                    onPocet?.(doc.numPages);
                }
            } catch (err) {
                console.error('PDF load failed:', err);
                if (!cancelled) setChyba(true);
            }
        };
        loadPdf();
        return () => { cancelled = true; };
    }, [url]);

    useEffect(() => {
        if (!pdfDoc || !canvasRef.current) return;
        let cancelled = false;
        setRendering(true);

        const render = async () => {
            try {
                const page = await pdfDoc.getPage(currentPage);
                const canvas = canvasRef.current;
                if (!canvas || cancelled) return;
                const ctx = canvas.getContext('2d');
                const containerWidth = containerRef.current?.clientWidth || window.innerWidth - 4;
                const dpr = window.devicePixelRatio || 1;
                const baseViewport = page.getViewport({ scale: 1 });
                // Fit to width, renderováno s vyšším rozlišením (dpr * headroom),
                // aby PDF zůstalo ostré i po přiblížení gestem na ~2.5x
                const oversample = 2.5;
                const scale = containerWidth / baseViewport.width;
                const viewport = page.getViewport({ scale: scale * dpr * oversample });

                canvas.width = viewport.width;
                canvas.height = viewport.height;
                canvas.style.width = `${containerWidth}px`;
                canvas.style.height = `${(baseViewport.height * scale)}px`;

                if (renderTaskRef.current) {
                    try { renderTaskRef.current.cancel(); } catch {}
                }

                const task = page.render({ canvasContext: ctx, viewport });
                renderTaskRef.current = task;
                await task.promise;
            } catch (err) {
                if (err?.name !== 'RenderingCancelledException') console.error(err);
            }
            if (!cancelled) setRendering(false);
        };
        render();
        return () => { cancelled = true; };
    }, [pdfDoc, currentPage]);

    if (chyba) {
        return (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', padding: 32, textAlign: 'center', color: 'var(--text-muted)' }}>
                <AlertCircle size={28} style={{ color: SECTION_COLOR, marginBottom: 10 }} />
                <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>Dokument se nepodařilo zobrazit.</span>
                <span style={{ fontSize: 13, marginTop: 4 }}>Zkus ho stáhnout.</span>
            </div>
        );
    }

    if (!pdfDoc) {
        return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 40, color: 'var(--text-muted)' }}>
                <Loader2 size={20} className="animate-spin" />
                <span style={{ marginLeft: 8, fontSize: 13 }}>Načítám PDF...</span>
            </div>
        );
    }

    const pagingStyle = {
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12,
        padding: '8px 0',
        borderTop: '1px solid var(--border-default, rgba(255,255,255,0.06))',
    };
    const pageBtnStyle = (disabled) => ({
        width: 32, height: 32, borderRadius: 6, border: 'none',
        background: disabled ? 'transparent' : 'var(--surface-hover, rgba(255,255,255,0.08))',
        color: disabled ? 'var(--text-disabled, rgba(255,255,255,0.2))' : 'var(--text-primary)',
        cursor: disabled ? 'default' : 'pointer',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
    });

    return (
        <div ref={containerRef} style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
            <div style={{ flex: 1, minHeight: 0, position: 'relative' }}>
                <ZoomPanViewer resetKey={currentPage}>
                    <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', overflow: 'auto', WebkitOverflowScrolling: 'touch', boxSizing: 'border-box', ...(odsazeni ? { paddingTop: odsazeni.nahore, paddingBottom: odsazeni.dole } : {}) }}>
                        <canvas ref={canvasRef} style={{ display: 'block', width: '100%', height: 'auto' }} />
                    </div>
                </ZoomPanViewer>
                {rendering && (
                    <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.15)', pointerEvents: 'none' }}>
                        <Loader2 size={20} className="animate-spin" style={{ color: 'var(--text-muted)' }} />
                    </div>
                )}
            </div>
            {totalPages > 1 && !rizeno && (
                <div style={pagingStyle}>
                    <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage <= 1} style={pageBtnStyle(currentPage <= 1)}>
                        <ChevronLeft size={16} />
                    </button>
                    <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-primary)', minWidth: 60, textAlign: 'center' }}>
                        {currentPage} / {totalPages}
                    </span>
                    <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage >= totalPages} style={pageBtnStyle(currentPage >= totalPages)}>
                        <ChevronRight size={16} />
                    </button>
                </div>
            )}
        </div>
    );
}

export default function SmernicPreviewModal({ smernice, verze, onClose, onPotvrdit, uzPodepsano, user, zIndex = 9999 }) {
    const { theme } = useTheme();
    const isDark = theme === 'dark' || (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);

    const [confirming, setConfirming] = useState(false);
    const [confirmed, setConfirmed] = useState(uzPodepsano);
    const [isMobile] = useState(() => window.innerWidth < 768);
    // Tablets (768–1024px) can't reliably render PDF in iframe — use canvas renderer
    const [useCanvasRenderer] = useState(() => window.innerWidth < 1025 || 'ontouchstart' in window);

    useEffect(() => { setConfirmed(uzPodepsano); }, [uzPodepsano]);

    if (!smernice || !verze) return null;

    const mime = verze.soubor_mime || '';
    const isPdf = mime === 'application/pdf';
    const isImage = mime.startsWith('image/');
    const canPreview = isPdf || isImage;
    // Chráněný disk nemá veřejnou URL — náhled jde přes autorizovaný endpoint
    const stahnoutUrl = `/api/bezpecnost/smernice/${smernice.id}/download${verze.id ? `?verze_id=${verze.id}` : ''}`;
    const fileUrl = `${stahnoutUrl}${verze.id ? '&' : '?'}inline=1`;
    const hasEmployee = !!user?.zamestnanec_id;

    const handleConfirm = async () => {
        if (confirming || confirmed) return;
        setConfirming(true);
        try {
            await onPotvrdit(smernice.id);
            setConfirmed(true);
        } catch {}
        setConfirming(false);
    };

    if (isMobile) {
        return createPortal(
            <CeloobrazovkovyNahled smernice={smernice} verze={verze} isDark={isDark} zIndex={zIndex}
                isPdf={isPdf} isImage={isImage} fileUrl={fileUrl} stahnoutUrl={stahnoutUrl}
                muzePotvrdit={hasEmployee && smernice.vyzaduje_podpis} confirmed={confirmed} confirming={confirming}
                onConfirm={handleConfirm} onClose={onClose} />,
            document.body
        );
    }

    const safeTop = '0px';

    const overlayStyle = {
        position: 'fixed', inset: 0, zIndex,
        background: 'rgba(0,0,0,0.6)',
        backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)',
        display: 'flex',
        alignItems: isMobile ? 'stretch' : 'center',
        justifyContent: 'center',
        padding: isMobile ? 0 : 16,
    };

    const panelStyle = {
        background: isDark ? 'rgba(15,15,25,0.97)' : 'var(--surface-raised)',
        border: isMobile ? 'none' : `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'var(--border-default)'}`,
        borderRadius: isMobile ? 0 : 16,
        boxShadow: isDark
            ? '0 8px 40px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,255,255,0.05)'
            : '0 8px 40px rgba(0,0,0,0.15)',
        width: '100%',
        maxWidth: isMobile ? '100%' : 900,
        height: isMobile ? '100%' : '85vh',
        maxHeight: isMobile ? '100%' : '90vh',
        display: 'flex', flexDirection: 'column',
        overflow: 'hidden',
        ...(isMobile ? { paddingTop: safeTop } : {}),
    };

    const btnBase = {
        display: 'flex', alignItems: 'center', gap: 6,
        padding: isMobile ? '8px 12px' : '8px 16px', borderRadius: 7,
        fontSize: 13, fontWeight: 500, cursor: 'pointer',
        border: 'none', transition: 'all 0.15s ease',
    };

    return createPortal(
        <div style={overlayStyle} onClick={onClose}>
            <div onClick={e => e.stopPropagation()} style={panelStyle}>
                {/* Header */}
                <div style={{
                    padding: isMobile ? '12px 14px' : '16px 20px',
                    borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'var(--border-default)'}`,
                    display: 'flex', alignItems: 'center', gap: isMobile ? 10 : 12, flexShrink: 0,
                }}>
                    <div style={{
                        width: isMobile ? 32 : 36, height: isMobile ? 32 : 36, borderRadius: 7,
                        background: 'rgba(239,68,68,0.12)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                    }}>
                        <FileText size={isMobile ? 15 : 18} style={{ color: SECTION_COLOR }} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                            <span style={{ fontSize: isMobile ? 13 : 15, fontWeight: 600, color: 'var(--text-primary)', wordBreak: 'break-word' }}>{smernice.nazev}</span>
                            <span style={{
                                padding: '2px 8px', borderRadius: 999, fontSize: 10, fontWeight: 600,
                                background: 'rgba(239,68,68,0.10)', color: SECTION_COLOR, border: '1px solid rgba(239,68,68,0.25)',
                            }}>v{verze.cislo_verze}</span>
                        </div>
                        {!isMobile && (
                            <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
                                {verze.soubor_nazev} · {formatFileSize(verze.soubor_velikost)} · {formatDate(verze.created_at)}
                                {verze.nahral && ` · ${verze.nahral.jmeno} ${verze.nahral.prijmeni}`}
                            </p>
                        )}
                    </div>
                    <button onClick={onClose} style={{
                        width: 32, height: 32, borderRadius: 6, border: 'none', cursor: 'pointer',
                        background: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)',
                        color: 'var(--text-muted)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                    }}>
                        <X size={16} />
                    </button>
                </div>

                {/* Body — Preview */}
                <div style={{ flex: '1 1 0', overflow: 'hidden', padding: canPreview ? 0 : 20, position: 'relative', minHeight: 0 }}>
                    {isPdf ? (
                        useCanvasRenderer ? (
                            <MobilePdfViewer url={fileUrl} />
                        ) : (
                            <iframe
                                src={fileUrl}
                                title={smernice.nazev}
                                style={{ width: '100%', height: '70vh', border: 'none' }}
                            />
                        )
                    ) : isImage ? (
                        <div style={{ width: '100%', height: '100%', minHeight: 0 }}>
                            <ZoomPanViewer resetKey={fileUrl}>
                                <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
                                    <img
                                        src={fileUrl}
                                        alt={smernice.nazev}
                                        draggable={false}
                                        style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', borderRadius: 6, pointerEvents: 'none' }}
                                    />
                                </div>
                            </ZoomPanViewer>
                        </div>
                    ) : (
                        <div style={{ textAlign: 'center', padding: '48px 20px' }}>
                            <div style={{
                                width: 56, height: 56, borderRadius: 12,
                                background: 'rgba(239,68,68,0.1)',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                margin: '0 auto 16px',
                            }}>
                                <AlertCircle size={28} style={{ color: SECTION_COLOR }} />
                            </div>
                            <p style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 4 }}>
                                Tento typ souboru nelze zobrazit v prohlížeči.
                            </p>
                            <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>
                                Stáhněte soubor a otevřete ho ve vhodné aplikaci.
                            </p>
                        </div>
                    )}
                </div>

                {/* Footer — na mobilu sticky dole */}
                <div style={{
                    padding: isMobile
                        ? '10px 14px 66px'
                        : '12px 20px',
                    borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'var(--border-default)'}`,
                    display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap',
                    flexShrink: 0,
                    background: isDark ? 'rgba(15,15,25,0.97)' : 'var(--surface-raised)',
                }}>
                    {/* Confirmed status */}
                    {confirmed && hasEmployee && smernice.vyzaduje_podpis && (
                        <span style={{
                            display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, fontWeight: 600,
                            color: '#10b981', marginRight: 'auto',
                        }}>
                            <CheckCircle2 size={14} /> Potvrzeno
                        </span>
                    )}

                    {/* Download */}
                    <a href={stahnoutUrl}
                        style={{
                            ...btnBase, textDecoration: 'none',
                            background: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)',
                            color: 'var(--text-secondary)',
                        }}>
                        <Download size={14} /> Stáhnout
                    </a>

                    {/* Confirm button */}
                    {hasEmployee && !confirmed && smernice.vyzaduje_podpis && (
                        <button onClick={handleConfirm} disabled={confirming}
                            style={{
                                ...btnBase, fontWeight: 600,
                                background: '#10b981', color: '#fff',
                                opacity: confirming ? 0.6 : 1,
                                cursor: confirming ? 'not-allowed' : 'pointer',
                            }}>
                            <CheckCircle2 size={14} />
                            {confirming ? 'Potvrzuji...' : (isMobile ? 'Beru na vědomí' : 'Přečteno a beru na vědomí')}
                        </button>
                    )}

                    {/* Close */}
                    <button onClick={onClose}
                        style={{
                            ...btnBase,
                            background: 'none',
                            border: `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : 'var(--border-default)'}`,
                            color: 'var(--text-muted)',
                        }}>
                        Zavřít
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
}

// ── Mobil: dokument přes celou obrazovku, ovládání plave nad ním ──
// Nahoře zavřít · název · stáhnout, dole stránkování a potvrzení. Tlačítka jsou
// ze skla, ať dokument nezakrývají víc, než musí; obsah má nahoře i dole
// odsazení, takže první i poslední řádek jde odscrollovat zpod nich.
function CeloobrazovkovyNahled({ smernice, verze, isDark, zIndex, isPdf, isImage, fileUrl, stahnoutUrl, muzePotvrdit, confirmed, confirming, onConfirm, onClose }) {
    const [strana, setStrana] = useState(1);
    const [pocet, setPocet] = useState(0);

    // Zavřít i systémovým Zpět by chtělo vlastní záznam v historii; zatím Escape pro klávesnice
    useEffect(() => {
        const naKlavesu = (e) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', naKlavesu);
        const puvodni = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => { window.removeEventListener('keydown', naKlavesu); document.body.style.overflow = puvodni; };
    }, [onClose]);

    const sklo = {
        background: isDark ? 'rgba(30,30,36,0.72)' : 'rgba(255,255,255,0.78)',
        backdropFilter: 'blur(20px) saturate(160%)', WebkitBackdropFilter: 'blur(20px) saturate(160%)',
        border: `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.08)'}`,
        boxShadow: '0 6px 24px rgba(0,0,0,0.35)',
        color: 'var(--text-primary)',
    };
    const kulate = { ...sklo, width: 42, height: 42, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, cursor: 'pointer' };
    const nahore = 'calc(env(safe-area-inset-top, 0px) + 10px)';
    const dole = 'calc(env(safe-area-inset-bottom, 0px) + 14px)';

    return (
        <div style={{ position: 'fixed', inset: 0, zIndex: Math.max(zIndex, 10000), background: isDark ? '#0B0B0F' : 'var(--surface-base)' }}>
            {/* Dokument */}
            <div style={{ position: 'absolute', inset: 0 }}>
                {isPdf ? (
                    <MobilePdfViewer url={fileUrl} strana={strana} onStrana={setStrana} onPocet={setPocet}
                        odsazeni={{ nahore: 'calc(env(safe-area-inset-top, 0px) + 64px)', dole: 'calc(env(safe-area-inset-bottom, 0px) + 84px)' }} />
                ) : isImage ? (
                    <ZoomPanViewer resetKey={fileUrl}>
                        <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '64px 12px 84px' }}>
                            <img src={fileUrl} alt={smernice.nazev} draggable={false} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', pointerEvents: 'none' }} />
                        </div>
                    </ZoomPanViewer>
                ) : (
                    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 32, textAlign: 'center' }}>
                        <AlertCircle size={28} style={{ color: SECTION_COLOR, marginBottom: 10 }} />
                        <p style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>Tento typ souboru nejde zobrazit v aplikaci.</p>
                        <p style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 4 }}>Stáhni ho tlačítkem vpravo nahoře.</p>
                    </div>
                )}
            </div>

            {/* Nahoře: zavřít · název · stáhnout */}
            <div style={{ position: 'absolute', top: nahore, left: 12, right: 12, display: 'flex', alignItems: 'center', gap: 8, pointerEvents: 'none' }}>
                <button onClick={onClose} aria-label="Zavřít" style={{ ...kulate, pointerEvents: 'auto' }}><X size={20} /></button>
                <div style={{ ...sklo, flex: 1, minWidth: 0, height: 42, borderRadius: 21, padding: '0 14px', display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{smernice.nazev}</span>
                    <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', flexShrink: 0 }}>v{verze.cislo_verze}</span>
                </div>
                <a href={stahnoutUrl} aria-label="Stáhnout" style={{ ...kulate, pointerEvents: 'auto', textDecoration: 'none' }}><Download size={18} /></a>
            </div>

            {/* Dole: stránkování a potvrzení — jen co je potřeba */}
            {(pocet > 1 || muzePotvrdit) && (
                <div style={{ position: 'absolute', bottom: dole, left: 12, right: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, pointerEvents: 'none' }}>
                    {pocet > 1 && (
                        <div style={{ ...sklo, height: 46, borderRadius: 23, padding: '0 4px', display: 'flex', alignItems: 'center', pointerEvents: 'auto' }}>
                            <button onClick={() => setStrana(p => Math.max(1, p - 1))} disabled={strana <= 1} aria-label="Předchozí strana"
                                style={{ width: 38, height: 38, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'none', color: 'var(--text-primary)', opacity: strana <= 1 ? 0.3 : 1 }}>
                                <ChevronLeft size={20} />
                            </button>
                            <span style={{ fontSize: 13, fontWeight: 700, minWidth: 44, textAlign: 'center' }}>{strana} / {pocet}</span>
                            <button onClick={() => setStrana(p => Math.min(pocet, p + 1))} disabled={strana >= pocet} aria-label="Další strana"
                                style={{ width: 38, height: 38, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'none', color: 'var(--text-primary)', opacity: strana >= pocet ? 0.3 : 1 }}>
                                <ChevronRight size={20} />
                            </button>
                        </div>
                    )}
                    {muzePotvrdit && (confirmed ? (
                        <div style={{ ...sklo, height: 46, borderRadius: 23, padding: '0 16px', display: 'flex', alignItems: 'center', gap: 6, color: '#10b981', fontSize: 13, fontWeight: 700, pointerEvents: 'auto' }}>
                            <CheckCircle2 size={16} /> Potvrzeno
                        </div>
                    ) : (
                        <button onClick={onConfirm} disabled={confirming}
                            style={{ height: 46, borderRadius: 23, padding: '0 20px', display: 'flex', alignItems: 'center', gap: 7, fontSize: 14, fontWeight: 700,
                                background: 'var(--brand)', color: '#1a1000', boxShadow: '0 6px 24px rgba(0,0,0,0.35)', opacity: confirming ? 0.6 : 1, pointerEvents: 'auto' }}>
                            {confirming ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle2 size={16} />}
                            {confirming ? 'Potvrzuji…' : 'Beru na vědomí'}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}
